import { defineStore } from 'pinia'
import { ref, Ref, computed } from 'vue'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { instance, getApiPrefix, FileType } from '@cognitivecredit/ui-utils'
import { syndtrakDisplayPath, syndtrakFolderPath } from '@/utils/dealConnectV2'
import { getApiDomain } from '@/config'
import store from '@/stores/index'
import {
  DealConnectV2Deal,
  DealConnectV2DealsResponse,
  DealConnectV2Document,
  DealConnectV2DocumentsResponse,
  DealConnectV2File,
  DealConnectV2FolderNote,
} from '@/components/dealconnect/dealconnect-v2/types'

dayjs.extend(utc)
dayjs.extend(timezone)

const getReq = async () => {
  const prefix = getApiPrefix('rac')
  return instance({ prefix, apiDomain: getApiDomain(prefix) })
}

const bookmarkProvider = (deal: DealConnectV2Deal) =>
  deal.source === 'managed_access' ? 'managed_access' : deal.datasite

const decodeDisplayPath = (filePath: string, source: string): string => {
  if (source !== 'managed_access') {
    return filePath
  }
  try {
    return decodeURIComponent(decodeURIComponent(filePath))
  } catch {
    return filePath
  }
}

// Convert a UTC timestamp into a naive local-time string in the user's preferred
// timezone so that downstream `medDate` formatting (which uses browser TZ) shows
// wall-clock time in that timezone instead of the browser's.
const shiftUpdatedAtToUserTimezone = (updatedAt: string | null): string => {
  if (!updatedAt) {
    return ''
  }
  const userTimezone = store.getters['User/preferences']?.timezone
  if (!userTimezone) {
    return updatedAt
  }
  const parsed = dayjs(updatedAt)
  if (!parsed.isValid()) {
    return updatedAt
  }
  return parsed.tz(userTimezone).format('YYYY-MM-DD[T]HH:mm:ss')
}

const normalizeFolderNotes = (
  notes: DealConnectV2FolderNote[] | undefined,
  dealId: string,
): DealConnectV2FolderNote[] =>
  (notes ?? []).flatMap((item) => {
    const path = syndtrakFolderPath(item.path ?? '')
    const note = item.note?.trim() ?? ''
    if (item.dealId !== dealId || !path || !note) {
      return []
    }
    return [{ dealId, path, note }]
  })

const toPrivateDocumentLabel = (value: boolean | null): 'private' | 'public' | null => {
  if (value === true) {
    return 'private'
  }
  if (value === false) {
    return 'public'
  }
  return null
}

const toFile = (doc: DealConnectV2Document, dealId: string, dealDatasite?: string): DealConnectV2File => {
  const datasite = doc.datasite || dealDatasite || ''
  const displayPath = syndtrakDisplayPath(decodeDisplayPath(doc.filePath, doc.source), datasite)
  const fileName = displayPath.split('/').pop() ?? displayPath
  const ext = fileName.split('.').pop()?.toLowerCase() ?? 'file'
  return {
    path: displayPath,
    name: fileName,
    filePath: doc.filePath,
    modifiedAt: doc.updatedAt ? shiftUpdatedAtToUserTimezone(doc.updatedAt) : '',
    type: ext as FileType,
    dealSiteId: doc.dealId || dealId,
    clientId: doc.clientId,
    source: doc.source,
    documentId: doc.documentId,
    privateDocument: toPrivateDocumentLabel(doc.privateDocument),
    datasite,
    downloadUrl: doc.downloadUrl || null,
    lastAccessedAt: doc.lastAccessedAt ? shiftUpdatedAtToUserTimezone(doc.lastAccessedAt) : null,
    note: doc.note?.trim() || null,
    dealName: doc.dealName ?? null,
    companyId: doc.companyId ?? null,
  }
}

const documentsForDeal = (body: DealConnectV2DocumentsResponse, dealId: string, dealDatasite?: string) => ({
  files: body.documents.map((doc) => toFile(doc, dealId, dealDatasite)),
  folderNotes: normalizeFolderNotes(body.folderNotes, dealId),
})

export const useDealConnectV2Store = defineStore('dealConnectV2', () => {
  const deals: Ref<DealConnectV2Deal[]> = ref([])
  const selectedDeal: Ref<DealConnectV2Deal | null> = ref(null)
  const documents: Ref<DealConnectV2File[]> = ref([])
  const folderNotes: Ref<DealConnectV2FolderNote[]> = ref([])
  const documentFeed: Ref<DealConnectV2File[]> = ref([])
  const loading: Ref<boolean> = ref(false)
  const documentsLoading: Ref<boolean> = ref(false)
  const documentFeedLoading: Ref<boolean> = ref(false)
  const error: Ref<string | null> = ref(null)
  const excludeDealsWithoutCompany: Ref<boolean> = ref(false)
  const pendingBookmarks: Ref<Record<string, true>> = ref({})

  // Company-scoped state (for individual company deals page)
  const companyDocuments: Ref<DealConnectV2File[]> = ref([])
  const companyFolderNotes: Ref<DealConnectV2FolderNote[]> = ref([])
  const companyDocumentsLoading: Ref<boolean> = ref(false)
  const companyDealsError: Ref<string | null> = ref(null)

  const fetchDeals = async () => {
    loading.value = true
    error.value = null
    try {
      const req = await getReq()
      const res = await req.get('rac/dealconnect/deals')
      const body = res.data as DealConnectV2DealsResponse
      deals.value = body.deals
    } catch (e) {
      const err = e as Error
      console.error('[ERROR] Fetching dealconnect deals', err)
      error.value = 'Failed to load deals'
    } finally {
      loading.value = false
    }
  }

  const dealsLength = computed(() => deals.value.length)

  const fetchDocuments = async (dealId: string) => {
    documentsLoading.value = true
    try {
      error.value = null
      const req = await getReq()
      const res = await req.get('rac/dealconnect/documents', { params: { dealId } })
      const body = res.data as DealConnectV2DocumentsResponse
      const dealDatasite =
        deals.value.find((deal) => deal.id === dealId)?.datasite ??
        (selectedDeal.value?.id === dealId ? selectedDeal.value.datasite : undefined)
      const dealDocuments = documentsForDeal(body, dealId, dealDatasite)
      documents.value = dealDocuments.files
      folderNotes.value = dealDocuments.folderNotes
    } catch (e) {
      const err = e as Error
      console.error('[ERROR] Fetching dealconnect documents', err)
      error.value = 'Failed to load documents'
    } finally {
      documentsLoading.value = false
    }
  }

  let documentFeedRequest = 0

  const fetchDocumentFeed = async (updatedAfter?: string) => {
    const request = ++documentFeedRequest
    documentFeedLoading.value = true
    try {
      error.value = null
      const req = await getReq()
      const res = updatedAfter
        ? await req.get('rac/dealconnect/documents', { params: { updatedAfter } })
        : await req.get('rac/dealconnect/documents')
      if (request !== documentFeedRequest) {
        return
      }
      const body = res.data as DealConnectV2DocumentsResponse
      documentFeed.value = body.documents.map((doc) => toFile(doc, doc.dealId))
    } catch (e) {
      if (request !== documentFeedRequest) {
        return
      }
      const err = e as Error
      console.error('[ERROR] Fetching dealconnect document feed', err)
      error.value = 'Failed to load documents'
    } finally {
      if (request === documentFeedRequest) {
        documentFeedLoading.value = false
      }
    }
  }

  const selectDeal = (deal: DealConnectV2Deal | null) => {
    selectedDeal.value = deal
    if (deal) {
      fetchDocuments(deal.id)
    } else {
      documents.value = []
      folderNotes.value = []
    }
  }

  const getSignedDownloadUrl = async (path: string | null | undefined): Promise<string> => {
    if (!path) {
      throw new Error('Download URL is not available')
    }
    const req = await getReq()
    const res = await req.get(path)
    return res.data.url
  }

  // hacky implementation: we get sent a preformatted download URL,
  // but the endpoint ending in /download actually downloads the file
  // whereas the endpoint without /download allows us to view the
  // file in the browser 🤷
  const getSignedViewUrl = async (path: string | null | undefined): Promise<string> => {
    if (!path) {
      throw new Error('Download URL is not available')
    }
    const req = await getReq()
    const res = await req.get(path.split('/download')[0])
    return res.data.url
  }

  const getManagedAccessDownloadUrl = async (dealId: string, clientId: string, filePath: string): Promise<string> => {
    const req = await getReq()
    const res = await req.get(`rac/clients/${clientId}/deals/${dealId}/${filePath}`)
    return res.request.responseURL
  }

  const getManagedAccessBulkDownloadUrls = async (clientId: string, companyId: string): Promise<string[]> => {
    const req = await getReq()
    const res = await req.get(`rac/clients/${clientId}/companies/${companyId}/deals/download`)
    const { downloadUrls } = res.data
    return downloadUrls.map((item: { downloadUrl: string }) => item.downloadUrl)
  }

  const getManagedAccessZipDownloadUrl = async (
    clientId: string,
    companyId: string,
    dealId: string,
  ): Promise<string> => {
    const req = await getReq()
    const res = await req.get(`rac/clients/${clientId}/companies/${companyId}/deals/download`)
    const { downloadUrls } = res.data
    const match = downloadUrls.find((item: { dealId: string; downloadUrl: string }) => item.dealId === dealId)
    if (!match) {
      throw new Error(`No managed access download URL found for deal ${dealId}`)
    }
    return match.downloadUrl
  }

  const dismissError = () => {
    error.value = null
  }

  // Mark a document as accessed by updating the lastAccessedAt timestamp
  // so no need to refetch the documents from the server
  const markDocumentAccessed = (file: DealConnectV2File) => {
    const accessedAt = shiftUpdatedAtToUserTimezone(new Date().toISOString())
    const stamp = (files: DealConnectV2File[]) =>
      files.map((doc) =>
        doc.dealSiteId === file.dealSiteId && doc.documentId === file.documentId && doc.filePath === file.filePath
          ? { ...doc, lastAccessedAt: accessedAt }
          : doc,
      )
    documents.value = stamp(documents.value)
    companyDocuments.value = stamp(companyDocuments.value)
    documentFeed.value = stamp(documentFeed.value)
  }

  const bookmarkKey = (deal: DealConnectV2Deal) => `${bookmarkProvider(deal)}:${deal.id}`

  const isBookmarkPending = (deal: DealConnectV2Deal) => !!pendingBookmarks.value[bookmarkKey(deal)]

  const toggleBookmark = async (deal: DealConnectV2Deal) => {
    const key = bookmarkKey(deal)
    if (pendingBookmarks.value[key]) {
      return
    }
    const provider = bookmarkProvider(deal)
    const next = !deal.bookmarked
    pendingBookmarks.value = { ...pendingBookmarks.value, [key]: true }
    error.value = null
    try {
      const req = await getReq()
      if (deal.bookmarked) {
        await req.delete(`rac/dealconnect/bookmarks/${encodeURIComponent(provider)}/${encodeURIComponent(deal.id)}`)
      } else {
        await req.put('rac/dealconnect/bookmarks', { provider, dealId: deal.id })
      }
      deal.bookmarked = next
      const listed = deals.value.find((item) => item.id === deal.id)
      if (listed && listed !== deal) {
        listed.bookmarked = next
      }
      if (selectedDeal.value && selectedDeal.value !== deal && selectedDeal.value.id === deal.id) {
        selectedDeal.value.bookmarked = next
      }
    } catch (e) {
      const err = e as Error
      console.error('[ERROR] Updating deal bookmark', err)
      error.value = 'Failed to update bookmark'
    } finally {
      const rest = { ...pendingBookmarks.value }
      delete rest[key]
      pendingBookmarks.value = rest
    }
  }

  const getDealsForCompany = (companyId: string): DealConnectV2Deal[] => {
    return deals.value.filter((deal) => deal.companyId?.includes(companyId))
  }

  const fetchDocumentsForCompany = async (companyId: string) => {
    companyDocumentsLoading.value = true
    companyDealsError.value = null
    try {
      if (!deals.value.length) {
        await fetchDeals()
      }
      const companyDeals = getDealsForCompany(companyId)
      const req = await getReq()
      const allDocs = await Promise.all(
        companyDeals.map(async (deal) => {
          const res = await req.get('rac/dealconnect/documents', { params: { dealId: deal.id } })
          const body = res.data as DealConnectV2DocumentsResponse
          return documentsForDeal(body, deal.id, deal.datasite)
        }),
      )
      companyDocuments.value = allDocs.flatMap((item) => item.files)
      companyFolderNotes.value = allDocs.flatMap((item) => item.folderNotes)
    } catch (e) {
      const err = e as Error
      console.error('[ERROR] Fetching dealconnect v2 documents for company', err)
      companyDealsError.value = 'Failed to load deal documents'
    } finally {
      companyDocumentsLoading.value = false
    }
  }

  return {
    deals,
    selectedDeal,
    documents,
    folderNotes,
    documentFeed,
    loading,
    documentsLoading,
    documentFeedLoading,
    dealsLength,
    error,
    fetchDeals,
    fetchDocuments,
    fetchDocumentFeed,
    selectDeal,
    getSignedDownloadUrl,
    getSignedViewUrl,
    getManagedAccessDownloadUrl,
    getManagedAccessBulkDownloadUrls,
    getManagedAccessZipDownloadUrl,
    dismissError,
    markDocumentAccessed,
    toggleBookmark,
    isBookmarkPending,
    excludeDealsWithoutCompany,
    companyDocuments,
    companyFolderNotes,
    companyDocumentsLoading,
    companyDealsError,
    getDealsForCompany,
    fetchDocumentsForCompany,
  }
})
